<template>
  <div
    class="date-request-instant-booking-form"
    data-test-id="date-request-instant-booking-form"
  >
    <div class="space-y-6">
      <div class="el-form-validation-margin">
        <label
          class="required form-label"
          :for="`instant-number-of-tickets-${formUpdateTrigger}`"
          >{{ t('dateRequest.instantBooking.numberOfTicketsLabel') }}</label
        >
        <div class="position-relative">
          <input
            :id="`instant-number-of-tickets-${formUpdateTrigger}`"
            ref="numberOfTicketsInput"
            v-model="localNumberOfTickets"
            data-test-id="date-request-instant-number-of-tickets"
            :placeholder="
              t('dateRequest.instantBooking.numberOfTicketsPlaceholder')
            "
            autocomplete="numberOfTickets"
            class="w-full rounded-lg border border-gray-200 bg-white p-2 mt-1"
            name="numberOfTickets"
            type="number"
            @change="onNumberOfTicketsCommit"
          />
        </div>
        <span
          v-if="invalidKeys.includes('numberOfTickets')"
          data-test-id="date-request-form-invalid"
          class="text-red text-sm"
        >
          <span
            v-if="
              form.numberOfTickets != null &&
              form.numberOfTickets < privateEventMinTickets &&
              privateEventMinTickets <
                eventGetters.getMinInstantBookingTickets(eventData)
            "
          >
            {{
              t(
                'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.privateEventError',
                { privateEventMinTickets }
              )
            }}
          </span>
          <span v-else>
            {{
              t(
                'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.dateRequestError',
                {
                  minTicketsDateRequest:
                    eventGetters.getMinInstantBookingTickets(eventData),
                }
              )
            }}
          </span>
        </span>
      </div>

      <div class="el-form-validation-margin">
        <label
          class="required form-label"
          :for="`instant-date-request-date-${formUpdateTrigger}`"
          >{{ t('dateRequest.instantBooking.preferredDateLabel') }}
        </label>
        <div class="relative mt-2 hidden w-full sm:block">
          <Datepicker
            :key="`instant-dp-${formUpdateTrigger}`"
            v-model="form.dates[dateSlotIndex]"
            model-type="yyyy-MM-dd'T'HH:mm"
            time-picker-inline
            :format-locale="de"
            format="dd.MM.yyyy, HH:mm"
            locale="de"
            :select-text="t('dateRequest.instantBooking.datePickerSelect')"
            text-input
            minutes-increment="30"
            :start-time="startTime"
            :disabled-dates="isDateDisabled"
            :disabled-week-days="getDisabledWeekDays()"
            :start-date="minDate"
            :placeholder="t('product.show.checkout.selectDate')"
            @open="emit('clearErrors', 'dates')"
          />
        </div>
        <div class="mt-2 relative w-full sm:hidden">
          <input
            :id="`instant-date-request-date-${formUpdateTrigger}`"
            v-model="form.dates[dateSlotIndex]"
            data-test-id="date-request-instant-date"
            :min="minDateTimeLocal"
            step="1800"
            :placeholder="t('product.show.checkout.selectDate')"
            type="datetime-local"
            :autocomplete="`dateRequestInstantDate${dateSlotIndex + 1}`"
            class="box-border min-w-0 w-full text-left rounded-lg border border-gray-200 bg-white ![padding-block-start:0.25rem] ![padding-block-end:0.25rem] ![padding-inline-start:0] ![padding-inline-end:0]"
            :name="`dateRequestInstantDate${dateSlotIndex + 1}`"
            @input="handleNativeDateInput($event)"
          />
        </div>
        <span
          v-if="invalidKeys.includes('dates')"
          data-test-id="date-request-form-invalid"
          class="text-red text-sm"
        >
          {{ errors.dates }}
        </span>
        <div
          v-if="
            isDateAvailable(form.dates[dateSlotIndex]) &&
            !isInstantDateOnDisabledWeekday &&
            !isInstantDateBeforeMinAdvance
          "
          class="mt-2 text-sm font-medium text-teal-600"
        >
          {{ t('dateRequest.availableDateHelperText') }}
        </div>
        <span
          v-if="isInstantDateBeforeMinAdvance"
          data-test-id="date-request-native-min-advance-invalid"
          class="mt-2 block text-red text-sm"
        >
          {{ t('dateRequest.nativeDatePicker.minAdvance') }}
        </span>
        <span
          v-else-if="isInstantDateOnDisabledWeekday"
          data-test-id="date-request-native-weekday-invalid"
          class="mt-2 block text-red text-sm"
        >
          {{ t('dateRequest.nativeDatePicker.disabledWeekday') }}
        </span>
      </div>

      <div
        v-if="showInstantReserveCallout"
        class="flex gap-3 rounded-lg bg-lime-100 px-4 py-3"
        data-test-id="date-request-instant-reserve-callout"
      >
        <PartyPopper
          class="h-6 w-6 shrink-0 text-lime-900"
          aria-hidden="true"
        />
        <div class="min-w-0">
          <p class="font-semibold text-lime-900">
            {{ t('dateRequest.instantBooking.calloutTitle') }}
          </p>
          <p class="mt-1 text-sm leading-relaxed text-lime-900">
            {{ t('dateRequest.instantBooking.calloutBody') }}
          </p>
        </div>
      </div>
      <Button
        v-if="
          !isDateAvailable(form.dates[dateSlotIndex]) &&
          !isInstantDateOnDisabledWeekday &&
          !isInstantDateBeforeMinAdvance
        "
        data-test-id="buy-box-date-request-submit-button"
        class="mt-4 w-full"
        :disabled="instantSubmitting"
        @click="handleInstantBookingSubmit"
      >
        <span>{{ t('dateRequest.instantBooking.buttonLabel') }}</span>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import dayjs from 'dayjs'
import { de } from 'date-fns/locale'
import { PartyPopper } from 'lucide-vue-next'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import type {
  DateRequestErrors,
  DateRequestInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type {
  EventDateType,
  EventDescription,
  EventDate,
} from '~/composables/types/event'
import { useCartWithSubdomainLogic } from '~/composables/useCartWithSubdomainLogic'
import { useFetchPricings } from '~/composables/useEventPricings'
import { buildInstantBookingCartItem } from '~/utils/buildInstantBookingCartItem'
import {
  isDateOnDisabledWeekday,
  roundToNearest30Minutes,
} from '~/utils/dateHelpers'

const dateSlotIndex = 0

interface DateRequestInstantFormModel {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

interface DateRequestInstantBookingFormProps {
  eventData: EventDescription
  availableDates?: EventDate[]
  errors?: DateRequestErrors
  invalidKeys?: DateRequestInvalidKeys
  privateEventMinTickets: number
  formUpdateTrigger?: number
}

const {
  eventData,
  availableDates = [],
  errors = {},
  invalidKeys = [],
  privateEventMinTickets,
  formUpdateTrigger = 0,
} = defineProps<DateRequestInstantBookingFormProps>()

const form = defineModel<DateRequestInstantFormModel>({ required: true })

const emit = defineEmits<{
  clearErrors: [field: string]
  numberOfTicketsUserChange: []
  instantBookingAdded: []
}>()

const { t } = useI18n()

const { isMobile } = useDevice()
const numberOfTicketsInput = ref<HTMLInputElement | null>(null)

const startTime = ref({ hours: 18, minutes: 0 })

const minDate = computed(() =>
  dayjs().add(
    eventGetters.getInstantBookingMinAdvanceQuantity(eventData),
    eventGetters.getInstantBookingMinAdvanceUnit(eventData).toLowerCase()
  )
)

const minDateTimeLocal = computed(() =>
  minDate.value.format('YYYY-MM-DDTHH:mm')
)

const getDisabledWeekDays = () =>
  [0, 1, 2, 3, 4, 5, 6].filter(
    (day) => !eventGetters.getInstantBookingWeekDays(eventData).includes(day)
  )

const isDateAvailable = (userDate: string) => {
  if (!userDate || !availableDates || availableDates.length === 0) {
    return false
  }

  return availableDates.some((availableDate) => {
    const userDateObj = dayjs(userDate)
    const availableDateObj = dayjs(availableDate.start)

    if (!userDateObj.isSame(availableDateObj, 'day')) {
      return false
    }

    const timeDiffMinutes = Math.abs(
      userDateObj.diff(availableDateObj, 'minutes')
    )
    return timeDiffMinutes <= 60
  })
}

const isInstantDateBeforeMinAdvance = computed(() => {
  const raw = form.value.dates[dateSlotIndex]?.trim() ?? ''
  if (!raw) {
    return false
  }
  const selected = dayjs(raw)
  if (!selected.isValid()) {
    return false
  }
  return selected.isBefore(minDate.value)
})

const isInstantDateOnDisabledWeekday = computed(() => {
  const raw = form.value.dates[dateSlotIndex]?.trim() ?? ''
  return isDateOnDisabledWeekday(raw, getDisabledWeekDays())
})

const showInstantReserveCallout = computed(() => {
  const n = form.value.numberOfTickets
  const ticketsFilled =
    n !== null &&
    n !== undefined &&
    Number.isFinite(Number(n)) &&
    Number(n) >= 1

  const raw = form.value.dates[dateSlotIndex]
  const dateStr = typeof raw === 'string' ? raw.trim() : ''
  const dateFilled = dateStr !== '' && dayjs(dateStr).isValid()

  return (
    ticketsFilled &&
    dateFilled &&
    !isDateAvailable(form.value.dates[dateSlotIndex]) &&
    !isInstantDateOnDisabledWeekday.value &&
    !isInstantDateBeforeMinAdvance.value
  )
})

const localNumberOfTickets = ref<string | number>(
  form.value?.numberOfTickets ?? ''
)
watch(
  () => form.value?.numberOfTickets,
  (val) => {
    localNumberOfTickets.value = val ?? ''
  }
)

const onNumberOfTicketsCommit = () => {
  const raw = localNumberOfTickets.value
  form.value.numberOfTickets = raw === '' || raw === null ? null : Number(raw)
  emit('clearErrors', 'numberOfTickets')
  emit('numberOfTicketsUserChange')
}

const handleNativeDateInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const inputValue = target?.value
  if (!inputValue) {
    return
  }
  const formatted = roundToNearest30Minutes(inputValue)
  if (formatted) {
    form.value.dates[dateSlotIndex] = formatted
    target.value = formatted
    const selected = dayjs(formatted)
    if (
      selected.isValid() &&
      !selected.isBefore(minDate.value) &&
      !isDateOnDisabledWeekday(formatted, getDisabledWeekDays())
    ) {
      emit('clearErrors', 'dates')
    }
  }
}

const isDateDisabled = (date: Date) =>
  dayjs(date).isBefore(minDate.value.startOf('day'))

const instantSubmitting = ref(false)

const resolveInstantPrivateFlag = (): boolean => {
  // Only allow public dates for instant booking
  return false
}

const handleInstantBookingSubmit = async () => {
  if (instantSubmitting.value) {
    return
  }
  const qty = form.value.numberOfTickets
  const userDate = form.value.dates[dateSlotIndex]?.trim()
  if (
    qty === null ||
    qty === undefined ||
    !Number.isFinite(Number(qty)) ||
    Number(qty) < 1 ||
    !userDate
  ) {
    toast(t('dateRequest.instantBooking.submitValidationError'), {
      type: 'error',
    })
    return
  }

  const quantity = Number(qty)
  const selectedStart = dayjs(userDate)
  if (!selectedStart.isValid()) {
    toast(t('dateRequest.instantBooking.submitValidationError'), {
      type: 'error',
    })
    return
  }

  if (selectedStart.isBefore(minDate.value)) {
    toast(t('dateRequest.nativeDatePicker.minAdvance'), {
      type: 'error',
    })
    return
  }

  if (isDateOnDisabledWeekday(userDate, getDisabledWeekDays())) {
    toast(t('dateRequest.nativeDatePicker.disabledWeekday'), {
      type: 'error',
    })
    return
  }

  instantSubmitting.value = true
  try {
    const isPrivate = resolveInstantPrivateFlag()
    const eventId = eventGetters.getId(eventData)
    const pricings = await useFetchPricings(ref(eventId), {
      dateId: ref(undefined),
      quantity: ref(quantity),
      isPrivate: ref(isPrivate),
    })

    if (!pricings) {
      toast(t('dateRequest.instantBooking.addToCartError'), { type: 'error' })
      return
    }

    const cartItem = buildInstantBookingCartItem(
      eventData,
      quantity,
      selectedStart.toISOString(),
      pricings,
      isPrivate
    )

    const { addItem } = useCartWithSubdomainLogic()
    await addItem(cartItem)
    const { toggleCartSidebar } = useUiState()
    toggleCartSidebar()
    emit('instantBookingAdded')
  } catch (error) {
    console.error('Instant booking add to cart failed:', error)
    toast(t('dateRequest.instantBooking.addToCartError'), { type: 'error' })
  } finally {
    instantSubmitting.value = false
  }
}

onMounted(() => {
  nextTick(() => {
    if (numberOfTicketsInput.value && !isMobile) {
      numberOfTicketsInput.value.focus()
    }
  })
})
</script>

<style>
.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}
</style>

<style lang="postcss" scoped>
.dp__menu {
  font-family: 'Lexend';
}
:deep(.dp__input) {
  @apply rounded-lg;
}
</style>
